import {
    IMPContextualNodeData, SlotPackage
} from '../../interface/DetailedBookingInterface';

interface ValidateImpressionsAvailabilityProps {
    nodeId: string,
    calculatedImpressions: number,
    contextualNodes: IMPContextualNodeData[],
    selectedSlotPackage: string | null,
    slotPackagesData: SlotPackage[],
    isApprovedBooking?: boolean,
    existingNodeImpressions?: number,
    isWaitlistEnabled?: boolean,
}

export const validateImpressionsAvailability = ({
    nodeId,
    calculatedImpressions,
    contextualNodes,
    selectedSlotPackage,
    slotPackagesData,
    isApprovedBooking,
    existingNodeImpressions,
    isWaitlistEnabled,
}: ValidateImpressionsAvailabilityProps
) => {
    // In the waitlist (Art Event value 1) a request may exceed available
    // impressions on purpose: the backend allocates what is available and queues
    // the remainder. Skip the availability block so over-requests can be submitted.
    if (isWaitlistEnabled) {
        return true;
    }

    const node = contextualNodes.find((n) => n.id === nodeId);
    if (!node?.imSegment || !selectedSlotPackage || !slotPackagesData.length) {
        return true;
    }

    const selectedPackage = slotPackagesData.find(
        (pkg) => pkg.slotPackageName === selectedSlotPackage
    );

    if (!selectedPackage) return true;

    const selectedSegment = selectedPackage.segments.find(
        ({ segmentName }) => segmentName === node.imSegment
    );

    if (!selectedSegment) return true;

    const impressionsBookedInCurrentSession = contextualNodes
        .filter(
            (otherNode) =>
                otherNode.id !== nodeId &&
                otherNode.imSegment === selectedSegment.segmentName // Only same segment matters for availability
        )
        .reduce((total, otherNode) => {
            const impressions = parseFloat(otherNode.impressions.toString()) || 0;
            return total + impressions;
        }, 0);

    // Mirror the backend's InventoryService.calculateAvailableImpressions exactly: floor the
    // overall*availability via integer division and clamp the segment headroom at 0. A float or
    // unclamped computation can allow a request the backend rejects (or reject one it allows) at
    // the boundary, producing a "looks fine, then 400s" mismatch.
    const segmentAvailable = Math.max(
        0,
        Math.floor((selectedSegment.overAllImpressions * selectedSegment.availability) / 100) -
        selectedSegment.impressionsBooked
    );
    const availableImpressions = segmentAvailable - impressionsBookedInCurrentSession;

    if (calculatedImpressions <= availableImpressions) return true;
    if (isApprovedBooking && existingNodeImpressions !== undefined && calculatedImpressions <= (availableImpressions + existingNodeImpressions)) return true;
    return false;
};
